import React, { forwardRef, useMemo, useRef, useState } from "react";
import { Loader, Select } from "@mantine/core";
import useRemoteSearch from "../../utils/useRemoteSearch";

// The value of the note at the foot of the list; it can't be picked
export const MORE_NOTE = "__more__";

// The note for a list that shows `shown` of `total` matches; null when it shows them all
export const moreNote = (shown, total, noun) =>
  total > shown
    ? `Showing ${shown} of ${total} ${noun}. Type to narrow the list.`
    : null;

/**
 * A Select that searches the server as you type, for lists too long to load whole: page "all"
 * stops at 200 rows (OD-1694). `fetchOptions(search)` answers { options: [{ value, label }],
 * total }; when total is more than it sent, a note at the foot of the list says so.
 *
 * `fixedOptions` are always listed first (an "Unassigned", say). `selectedOption`
 * ({ value, label }) labels the value until the results include it, as for a saved value.
 * `component` is the Select to render (a styled wrapper of Mantine's). onChange gets the value
 * and the picked option.
 */
const RemoteSearchSelect = forwardRef(
  (
    {
      fetchOptions,
      value,
      onChange,
      fixedOptions = [],
      selectedOption,
      noun = "results",
      nothingFound = "Nothing found",
      itemComponent: ItemComponent,
      component: Component = Select,
      disabled,
      rightSection,
      ...selectProps
    },
    ref
  ) => {
    const [opened, setOpened] = useState(false);
    // The last option picked, so its label stays once a new search no longer lists it
    const picked = useRef(null);
    const { setSearch, result, loading } = useRemoteSearch(fetchOptions, {
      enabled: opened && !disabled,
    });

    const found = result?.options || [];
    const current = [picked.current, selectedOption].find(
      (option) => option?.value != null && option.value === value
    );
    const unlisted =
      current && ![...fixedOptions, ...found].some((o) => o.value === current.value);
    const data = [...fixedOptions, ...(unlisted ? [current] : []), ...found];
    const note = moreNote(found.length, result?.total || 0, noun);
    if (note) data.push({ value: MORE_NOTE, label: note, disabled: true });

    const selectedLabel = data.find((option) => option.value === value)?.label;

    // A custom item component draws the options; the note stays plain text
    const Item = useMemo(
      () =>
        ItemComponent &&
        forwardRef((props, itemRef) =>
          props.value === MORE_NOTE ? (
            <div ref={itemRef} className={props.className} data-disabled>
              {props.label}
            </div>
          ) : (
            <ItemComponent ref={itemRef} {...props} />
          )
        ),
      [ItemComponent]
    );

    return (
      <Component
        ref={ref}
        {...selectProps}
        {...(Item ? { itemComponent: Item } : {})}
        data={data}
        value={value}
        onChange={(picks) => {
          const option = data.find((o) => o.value === picks) || null;
          picked.current = option;
          onChange?.(picks, option);
        }}
        disabled={disabled}
        searchable
        // The server has already matched the search; Mantine's filter would hide matches whose
        // label doesn't contain it (a business name matched by the provider name, say)
        filter={() => true}
        // Mantine puts the picked label in the search box: that is not a search
        onSearchChange={(text) => setSearch(text === selectedLabel ? "" : text)}
        onDropdownOpen={() => setOpened(true)}
        onDropdownClose={() => setOpened(false)}
        nothingFound={loading ? "Searching…" : nothingFound}
        rightSection={opened && loading ? <Loader size="xs" /> : rightSection}
      />
    );
  }
);

export default RemoteSearchSelect;
